/**
 * PermissionRoute - Epic 27: Controle de Acesso baseado em permissoes
 *
 * Componente de guarda de rota que verifica:
 * 1. Autenticacao (redireciona para /login se nao autenticado)
 * 2. Troca de senha obrigatoria (redireciona para /trocar-senha)
 * 3. Permissao pode_visualizar para a tela correspondente a rota
 *
 * Rotas sem mapeamento em ROUTE_TELA_MAP sao acessiveis a todos autenticados.
 * MASTER role tem bypass automatico (tratado em authStore.podeVisualizar).
 *
 * Substitui o antigo MasterOnlyRoute que bloqueava TODOS os nao-MASTER.
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ShieldX, LogOut, ArrowLeft } from 'lucide-react';
import useAuthStore from '../stores/authStore';
import { resolveTelaFromPath } from '../constants/routePermissions';

export const PermissionRoute = () => {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const deveTrocarSenha = useAuthStore((state) => state.deveTrocarSenha);
  const podeVisualizar = useAuthStore((state) => state.podeVisualizar);
  const { pathname } = useLocation();

  // 1. Nao autenticado → login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // 2. Deve trocar senha → tela de troca
  if (deveTrocarSenha) {
    return <Navigate to="/trocar-senha" replace />;
  }

  // 3. Resolver tela para a rota atual
  const telaCode = resolveTelaFromPath(pathname);

  // Sem mapeamento = rota livre para todos autenticados
  if (!telaCode) {
    return <Outlet />;
  }

  // 4. Verificar pode_visualizar (MASTER bypass interno)
  if (podeVisualizar(telaCode)) {
    return <Outlet />;
  }

  // 5. Acesso negado
  return <AccessDeniedView user={user} />;
};

/**
 * Tela de acesso negado - exibida quando usuario nao tem pode_visualizar
 */
function AccessDeniedView({ user }: { user: any }) {
  const handleLogout = () => {
    useAuthStore.getState().logout();
    window.location.href = '/login';
  };

  const handleGoBack = () => {
    window.location.href = '/selection-client';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-8">
          {/* Icone */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
              <ShieldX className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
          </div>

          {/* Titulo */}
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-3">
            Acesso Negado
          </h1>

          {/* Mensagem */}
          <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
            Voce nao tem permissao para acessar esta pagina.
            Entre em contato com o administrador para solicitar acesso.
          </p>

          {/* Info do usuario */}
          <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Usuario logado:</strong> {user?.email || 'Desconhecido'}
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              <strong>Perfil:</strong> {user?.role || 'Desconhecido'}
            </p>
          </div>

          {/* Botoes */}
          <div className="flex flex-col gap-3">
            <button
              onClick={handleGoBack}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-4 rounded-lg transition-colors font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar ao inicio
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 py-2.5 px-4 rounded-lg transition-colors font-medium"
            >
              <LogOut className="w-4 h-4" />
              Fazer logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
